"use client";

/**
 * CollabV3ConditionalFee 컴포넌트
 * @description 조건부 참가비의 진행 — `18 joined • 12 more to go`, 최소 인원까지의 막대, `Min 30` ·
 *   `Max 45`, 단계 안내(판정 마감 날짜 포함). 앱 `CollabConditionalFeeCard` 의 웹 대응이다.
 *   미달로 끝났거나 취소됐으면 인원을 숨긴다(앱과 같다).
 * @example <CollabV3ConditionalFee fee={detail.fee} />
 */
import { memo, useMemo } from "react";

import { collabV3CardStyles as styles } from "./CollabV3Card.styles";
import type { CollabV3ConditionalFeeProps } from "./CollabV3Card.types";
import CollabV3ProgressBar from "./CollabV3ProgressBar";
import { useCollabV3TimeZone } from "./CollabV3TimeZone";

import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import type { CollabV3ConditionalState } from "../../entities";
import { formatDay } from "../../utils/collabV3Format";

const copy = COLLAB_V3_CARD_COPY.fee.cond;
const PERCENT = 100;
const HIDES_PROGRESS: CollabV3ConditionalState[] = ["notReached", "cancelled"];

function infoOf(state: CollabV3ConditionalState, date: string): string | null {
  switch (state) {
    case "collecting":
      return copy.info.collecting(date);
    case "minReached":
      return copy.info.minReached(date);
    case "full":
      return copy.info.full(date);
    case "committed":
      return copy.info.committed;
    case "notReached":
      return copy.info.notReached;
    case "closed":
      return copy.info.closed;
    default:
      return null;
  }
}

const CollabV3ConditionalFee = memo<CollabV3ConditionalFeeProps>(({ fee }) => {
  const timeZone = useCollabV3TimeZone();
  const state = fee.conditionalState ?? "collecting";
  const date = useMemo(
    () => (fee.closesAt ? formatDay(fee.closesAt, timeZone) : ""),
    [fee.closesAt, timeZone],
  );
  const info = infoOf(state, date);
  const toMin = Math.max(fee.minCount - fee.reservedCount, 0);
  const capacity =
    fee.remainingSeats === null ? null : fee.reservedCount + fee.remainingSeats;
  const percent =
    fee.minCount > 0 ? (fee.reservedCount / fee.minCount) * PERCENT : PERCENT;

  return (
    <div className={styles.stack}>
      {HIDES_PROGRESS.includes(state) ? null : (
        <>
          <p className={styles.body}>
            {copy.count(fee.reservedCount)}
            {state === "collecting" && toMin > 0 ? ` • ${copy.toMin(toMin)}` : ""}
          </p>
          <CollabV3ProgressBar percent={percent} label={copy.minLabel(fee.minCount)} />
          <p className={styles.meta}>
            <span>{copy.minLabel(fee.minCount)}</span>
            {capacity !== null ? <span>{copy.maxLabel(capacity)}</span> : null}
          </p>
        </>
      )}
      {info ? <p className={styles.info}>{info}</p> : null}
    </div>
  );
});

CollabV3ConditionalFee.displayName = "CollabV3ConditionalFee";

export default CollabV3ConditionalFee;
